import React, { useMemo, useRef, useState } from "react";
import {
    Box,
    Typography,
    IconButton,
    Avatar,
    Paper,
    useMediaQuery,
    useTheme,
    Tooltip,
    Stack,
    Button,
    Modal,
    Chip,
} from "@mui/material";
import {
    ArrowBackIos,
    ArrowForwardIos,
    AspectRatio,
    Close,
    GridView,
    TableView,
    Visibility,
    Wysiwyg
} from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import CustomToolbar from "../utils/customToolbar";

const CustomCarousel = ({
    columns,
    title,
    data = [],
    activeColor = "#FFD166",
    inactiveColor = "#EAEBDC",
    filterColor = "#183021ff",
}) => {
    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const isMd = useMediaQuery(theme.breakpoints.between("sm", "lg"));
    const [selected, setSelected] = useState("Asc");
    const [openModal, setOpenModal] = useState(false);
    
    const originalDataRef = useRef([]);
    if (originalDataRef.current !== data && data.length) {
        originalDataRef.current = data;
    }

    React.useEffect(() => {
        setActiveIndex(0);
    }, [selected]);

    const [activeIndex, setActiveIndex] = useState(0);

    // Sorted data based on selected order, always from originalData
    React.useEffect(() => {
        if (data?.length) {
            originalDataRef.current = data;
            setActiveIndex(0);     // reset position
        }
    }, [data]);

    const sortedData = useMemo(() => {
        if (selected === "Desc")
            return [...originalDataRef.current].sort((a, b) => a.count - b.count);

        if (selected === "Asc")
            return [...originalDataRef.current].sort((a, b) => b.count - a.count);

        return originalDataRef.current;
    }, [selected, data]);

    // DataGrid rows with unique IDs
    const rows = useMemo(() =>
        originalDataRef.current.map((item, index) => ({
            id: index,
            ...item,
        }))
        , [originalDataRef.current]);

    // Responsive visible count
    const visibleCount = isXs ? 1 : isMd ? 3 : 5;

    const handlePrev = () => {
        setActiveIndex((prev) => (prev - 1 + sortedData.length) % sortedData.length);
    };

    const handleNext = () => {
        setActiveIndex((prev) => (prev + 1) % sortedData.length);
    };

    const handleOpenModal = () => {
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    // Calculate visible items around activeIndex
    const half = Math.floor(visibleCount / 2);
    const visibleItems = sortedData.map((_, i) => {
        let diff = (i - activeIndex + sortedData.length) % sortedData.length;
        if (diff > sortedData.length / 2) diff -= sortedData.length;
        return diff;
    });

    return (
        <>
            <Box display={'flex'} justifyContent={'space-between'}>
                <Stack direction="row" alignItems="center" spacing={1}>
                     <Typography variant="subtitle" fontWeight="bold">
                            {title}
                        </Typography>
                        
                    </Stack>
                <Stack direction="row" alignItems="center" spacing={2} >
                   <Tooltip title='Open Grid'>
                            <Wysiwyg 
                            onClick={handleOpenModal} 
                            sx={{
                                cursor: 'pointer'
                            }}/>
                        </Tooltip>
                    <Button
                        size="small"
                        variant={selected === "Asc" ? "contained" : "outlined"}
                        sx={{
                            fontSize: 14,
                            textTransform: "none",
                            borderRadius: 3,
                            bgcolor: selected === "Asc" ? filterColor : undefined,
                            color: selected === "Asc" ? "#ffffffff" : undefined,
                            "&:hover": selected === "Asc"
                                ? { bgcolor: filterColor }
                                : {},
                            borderColor: selected === "Asc" ? filterColor : undefined,
                        }}
                        onClick={() => setSelected("Asc")}
                    >
                        Ascending
                    </Button>

                    <Button
                        size="small"
                        variant={selected === "Desc" ? "contained" : "outlined"}
                        sx={{
                            fontSize: 14,
                            textTransform: "none",
                            borderRadius: 3,
                            bgcolor: selected === "Desc" ? filterColor : undefined,
                            color: selected === "Desc" ? "#ffffffff" : undefined,
                            "&:hover": selected === "Desc"
                                ? { bgcolor: filterColor }
                                : {},
                            borderColor: selected === "Desc" ? filterColor : undefined,
                        }}
                        onClick={() => setSelected("Desc")}
                    >
                        Descending
                    </Button>
                </Stack>
            </Box>
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    overflow: "hidden",
                    mt: 3,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    py: 8,
                }}
            >
                <IconButton onClick={handlePrev} sx={{ zIndex: 12, mr: 4, mt: 2 }}>
                    <ArrowBackIos />
                </IconButton>

                <Box
                    sx={{
                        position: "relative",
                        height: 180,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-around",
                        width: "90%",
                    }}
                >
                    {sortedData.map((item, index) => {
                        const diff = visibleItems[index];
                        if (Math.abs(diff) > half) return null;

                        const isActive = diff === 0;
                        const scale = isActive ? 1.1 : 0.95;
                        const opacity = isActive ? 1 : 0.7;
                        const zIndex = 10 - Math.abs(diff);

                        // spacing based on index difference
                        const cardGap = isXs ? 180 : 210;
                        const translateX = diff * cardGap;

                        return (
                            <Box
                                key={index}
                                sx={{
                                    position: "absolute",
                                    transform: `translateX(${translateX}px) scale(${scale})`,
                                    transition: "all 0.5s ease",
                                    opacity,
                                    zIndex,
                                }}
                            >
                                <Box sx={{ position: "relative", display: "inline-block", mt: 2 }}>
                                    {/* Avatar overlapping top-left */}
                                    <Avatar
                                        src={item.img || "/placeholder-avatar.png"}
                                        sx={{
                                            width: isXs ? 40 : 50,
                                            height: isXs ? 40 : 50,
                                            position: "absolute",
                                            top: -20,
                                            left: -20,
                                            border: "3px solid white",
                                            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                                            zIndex: 2,
                                        }}
                                    />
                                    <Paper
                                        elevation={isActive ? 6 : 2}
                                        sx={{
                                            px: 3,
                                            py: 1,
                                            bgcolor: isActive ? activeColor : inactiveColor,
                                            borderRadius: 3,
                                            width: isXs ? 150 : 180,
                                            textAlign: "center",
                                            boxShadow: isActive
                                                ? "0 6px 14px rgba(0,0,0,0.2)"
                                                : "0 2px 6px rgba(0,0,0,0.1)",
                                            transition: "all 0.3s ease",
                                            mx: "auto",
                                        }}
                                    >
                                        <Tooltip title={item.level} arrow>
                                            <Typography
                                                variant="body"
                                                fontWeight="bold"
                                                textTransform="uppercase"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    maxWidth: "160px",
                                                    mx: "auto",
                                                    cursor: "default",
                                                }}
                                            >
                                                {item.level}
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title={item.pointName || "Point"} arrow>
                                            <Box display="block">
                                            <Typography
                                                variant="title"
                                                fontWeight="bold"
                                                textTransform="uppercase"
                                                sx={{
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    maxWidth: "160px",
                                                    mx: "auto",
                                                    cursor: "default",
                                                }}
                                            >
                                                {item.count}
                                            </Typography>
                                            </Box>
                                        </Tooltip>
                                    </Paper>
                                </Box>

                                {isActive && (
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            px: 1.5,
                                            py: 1.5,
                                            mt: 1,
                                            borderRadius: 2,
                                            width: isXs ? 150 : 180,
                                            textAlign: "left",
                                            backgroundColor: "background.main",
                                            textOverflow: "ellipsis",
                                            cursor: "default"
                                        }}
                                    >
                                        <Tooltip title={item.department} arrow>
                                            <Typography variant="body" color="text.secondary" display="block">
                                                Department: {item.department ?? "-"}
                                            </Typography>
                                        </Tooltip>
                                        <Tooltip title={item.designation} arrow>
                                            <Typography variant="body" color="text.secondary" display="block">
                                                Designation: {item.designation ?? "-"}
                                            </Typography></Tooltip>
                                        <Tooltip title={item.missionsCreated} arrow>
                                            {item.missionsCreated !== undefined && (
                                                <Typography variant="body" color="text.secondary" display="block">
                                                    Missions Created: {item.missionsCreated}
                                                </Typography>
                                            )}</Tooltip>
                                        {item.totalPointsCreated !== undefined && (
                                            <Typography variant="body" color="text.secondary" display="block">
                                                Total Points Created: {item.totalPointsCreated}
                                            </Typography>
                                        )}
                                        <Tooltip title={item.pointsAssigned} arrow>
                                            {item.pointsAssigned !== undefined && (
                                                <Typography variant="body" color="text.secondary" display="block">
                                                    Points Assigned: {item.pointsAssigned}
                                                </Typography>
                                            )}</Tooltip>
                                    </Paper>
                                )}
                            </Box>
                        );
                    })}
                </Box>

                <IconButton onClick={handleNext} sx={{ zIndex: 12, ml: 4, mt: 2  }}>
                    <ArrowForwardIos />
                </IconButton>
            </Box>

            {/* Modal for Data Table */}
            <Modal
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="data-table-modal"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 2,
                    
                }}
            >
                <Paper
                    sx={{
                        width: '95%',
                        maxWidth: 1400,
                        maxHeight: '90vh',
                        overflow: 'hidden',
                        p: 3,
                        position: 'relative',
                        backgroundColor: "#FFFEFA"
                    }}
                >
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            zIndex: 1,
                        }}
                    >
                        <Close />
                    </IconButton>

                    <Box sx={{ height: 500, width: '100%', mt: 2 }}>
                        <DataGrid
                            rows={rows}
                            columns={columns}
                            showToolbar
                            slots={{ toolbar: CustomToolbar }}
                            slotProps={{
                                toolbar: {
                                    title: title,
                                    showDownload: true
                                },
                            }}
                            initialState={{
                                sorting: {
                                    sortModel: [{ field: 'count', sort: 'desc' }],
                                },
                            }}
                            hideFooterPagination
                            hideFooter
                            disableRowSelectionOnClick
                            sx={{
                                border: 'none',
                                borderColor: 'divider',
                                '& .MuiDataGrid-cell:hover': {
                                    backgroundColor: 'action.hover',
                                },
                                '& .MuiDataGrid-row:hover': {
                                    backgroundColor: 'action.hover',
                                },
                            }}
                        />
                    </Box>

                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <Typography variant="body" color="text.secondary" display="block">
                            Total Records: {rows.length}
                        </Typography>
                    </Box>
                </Paper>
            </Modal>
        </>

    );
};

export default CustomCarousel;